import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  TableRow,
  TableCell,
  tableCellClasses,
  Checkbox,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

// Icons
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import TimerIcon from "@mui/icons-material/Timer";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import PaymentIcon from "@mui/icons-material/Payment";
import HistoryIcon from "@mui/icons-material/History";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";

// Components
import CommonTable from "../Components/Common/CommonTable";
import CustomPagination from "../Components/Common/CommonPagination";
import DataNotFound from "../Components/Common/DataNotFound";
import { showLoader } from "../Redux/Slices/loaderSlice";
import ErrorHandler from "../Utils/handler";

// Actions
import {
  getRenewalList,
  getMemberRenewalDetails,
  getReminderStats,
  exportRenewalList,
  sendRenewalTemplateReminder,
  sendBulkTemplateReminders,
  getReminderHistory,
} from "../Redux/Actions/action";

dayjs.extend(relativeTime);

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme?.palette?.bgLightWhite?.main,
    fontSize: 14,
    textAlign: "center",
    lineHeight: "1.2rem",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "12px !important",
    textAlign: "center",
  },
}));

const StatCard = styled(Paper)(({ theme, color }) => ({
  padding: "16px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
  border: `1px solid ${color}30`,
  transition: "transform 0.2s, box-shadow 0.2s",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 4px 12px ${color}20`,
  },
}));

const StatusChip = styled(Chip)(({ statustype }) => {
  const colors = {
    active: { bg: "#dcfce7", color: "#16a34a", border: "#86efac" },
    expiring_soon: { bg: "#fef3c7", color: "#d97706", border: "#fcd34d" },
    grace_period: { bg: "#ffedd5", color: "#ea580c", border: "#fdba74" },
    expired: { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
    never_paid: { bg: "#e5e7eb", color: "#4b5563", border: "#9ca3af" },
  };
  const c = colors[statustype] || colors.active;
  return {
    backgroundColor: c.bg,
    color: c.color,
    border: `1px solid ${c.border}`,
    fontWeight: 600,
    fontSize: "12px",
  };
});

const DaysChip = styled(Chip)(({ days }) => {
  let bg, color, border;
  const d = parseInt(days);
  
  if (d === null || isNaN(d)) {
    bg = "#e5e7eb"; color = "#4b5563"; border = "#9ca3af";
  } else if (d < 0) {
    bg = "#fee2e2"; color = "#dc2626"; border = "#fca5a5";
  } else if (d <= 7) {
    bg = "#fee2e2"; color = "#dc2626"; border = "#fca5a5";
  } else if (d <= 15) {
    bg = "#ffedd5"; color = "#ea580c"; border = "#fdba74";
  } else if (d <= 30) {
    bg = "#fef3c7"; color = "#d97706"; border = "#fcd34d";
  } else {
    bg = "#dcfce7"; color = "#16a34a"; border = "#86efac";
  }
  
  return {
    backgroundColor: bg,
    color: color,
    border: `1px solid ${border}`,
    fontWeight: 600,
    fontSize: "12px",
    minWidth: "80px",
  };
});

const Reminders = () => {
  const dispatch = useDispatch();

  // State
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({
    members: { total: 0, expiringIn7Days: 0, expiringIn15Days: 0, expiringIn30Days: 0, inGracePeriod: 0, expired: 0, neverPaid: 0, active: 0 },
    reminders: { today: { total: 0 }, thisWeek: { total: 0 }, thisMonth: { total: 0 } }
  });
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [memberDetails, setMemberDetails] = useState(null);
  const [detailTabValue, setDetailTabValue] = useState(0);
  const [selectedTemplateType, setSelectedTemplateType] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [recentLogs, setRecentLogs] = useState([]);
  const [lastSendResult, setLastSendResult] = useState(null);
  const [linkGeneratorOpen, setLinkGeneratorOpen] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  
  const [filters, setFilters] = useState({
    daysFilter: "",
    statusFilter: "all",
    search: "",
    sortBy: "daysRemaining",
    sortOrder: "asc",
  });

  const daysFilterOptions = [
    { value: "", label: "All" },
    { value: "7", label: "Expiring in 7 days" },
    { value: "15", label: "Expiring in 15 days" },
    { value: "30", label: "Expiring in 30 days" },
    { value: "60", label: "Expiring in 60 days" },
    { value: "90", label: "Expiring in 90 days" },
  ];

  const statusFilterOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "expiring_soon", label: "Expiring Soon" },
    { value: "grace_period", label: "Grace Period" },
    { value: "expired", label: "Expired" },
    { value: "never_paid", label: "Never Paid" },
  ];

  // Fetch members
  const fetchMembers = async () => {
    setLoading(true);
    dispatch(showLoader(true));
    try {
      const body = {
        page,
        limit: rowsPerPage,
        ...filters,
      };
      const result = await dispatch(getRenewalList(body));
      if (ErrorHandler(result, false)) {
        setMembers(result?.payload?.data?.members || []);
        setTotalCount(result?.payload?.data?.pagination?.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Failed to fetch members");
    } finally {
      setLoading(false);
      dispatch(showLoader(false));
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const result = await dispatch(getReminderStats());
      if (ErrorHandler(result, false)) {
        setStats(result?.payload?.data || stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };


  // Fetch member details
  const fetchMemberDetails = async (memberId) => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(getMemberRenewalDetails(memberId));
      if (ErrorHandler(result, false)) {
        setMemberDetails(result?.payload?.data);
        setDetailTabValue(0);
        setDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching member details:", error);
      toast.error("Failed to fetch member details");
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Send single reminder
  const handleSendReminder = async (member) => {
    setSelectedMember(member);
    setSendDialogOpen(true);
  };

  const confirmSendReminder = async () => {
    if (!selectedMember) return;
    
    setSendingReminder(true);
    setLastSendResult(null);
    try {
      // Use new template-based reminder with auto-selection based on days remaining
      const result = await dispatch(sendRenewalTemplateReminder({
        userId: selectedMember._id,
        templateType: selectedTemplateType,
        gracePeriodDays: 30
      }));
      
      if (ErrorHandler(result, true)) {
        const data = result?.payload?.data;
        const templateUsed = data?.templateUsed || "auto";
        const paymentLink = data?.paymentLink || null;
        const sendResult = data?.sendResult || {};
        
        setLastSendResult({
          success: true,
          memberName: selectedMember.fullName,
          templateUsed,
          paymentLink,
          messageStatus: sendResult?.response?.message?.message_status || 'queued',
          sentAt: new Date().toISOString()
        });
        
        toast.success(`WhatsApp reminder sent successfully! Template: ${templateUsed}`);
        setSendDialogOpen(false);
        setSelectedTemplateType("auto");
        fetchMembers();
        fetchStats();
      } else {
        setLastSendResult({
          success: false,
          memberName: selectedMember.fullName,
          error: result?.payload?.message || 'Failed to send',
          sentAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error("Error sending reminder:", error);
      setLastSendResult({
        success: false,
        memberName: selectedMember.fullName,
        error: error.message || 'Failed to send reminder',
        sentAt: new Date().toISOString()
      });
      toast.error("Failed to send reminder");
    } finally {
      setSendingReminder(false);
    }
  };

  // Send bulk reminders using Meta-approved templates
  const handleBulkSend = async () => {
    if (selectedMembers.length === 0) {
      toast.warning("Please select members to send reminders");
      return;
    }
    
    setSendingReminder(true);
    setLastSendResult(null);
    dispatch(showLoader(true));
    try {
      // Use new template-based bulk reminders with auto-selection
      const result = await dispatch(sendBulkTemplateReminders({
        userIds: selectedMembers,
        templateType: "auto",
        gracePeriodDays: 30
      }));
      
      if (ErrorHandler(result, true)) {
        const data = result?.payload?.data;
        setLastSendResult({
          success: true,
          isBulk: true,
          successful: data?.successful || 0,
          failed: data?.failed || 0,
          total: selectedMembers.length,
          sentAt: new Date().toISOString()
        });
        toast.success(`Template reminders sent: ${data?.successful || 0} successful, ${data?.failed || 0} failed`);
        setSelectedMembers([]);
        fetchMembers();
        fetchStats();
      } else {
        setLastSendResult({
          success: false,
          isBulk: true,
          error: result?.payload?.message || 'Failed to send bulk reminders',
          sentAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error("Error sending bulk reminders:", error);
      setLastSendResult({
        success: false,
        isBulk: true,
        error: error.message || 'Failed to send bulk reminders',
        sentAt: new Date().toISOString()
      });
      toast.error("Failed to send bulk reminders");
    } finally {
      setSendingReminder(false);
      dispatch(showLoader(false));
    }
  };

  // Export list
  const handleExport = async () => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(exportRenewalList({
        ...filters,
        format: "csv"
      }));
      
      if (result?.payload) {
        // Create download link
        const blob = new Blob([result.payload], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `renewal_list_${dayjs().format("YYYY-MM-DD")}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Export successful!");
      }
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Failed to export data");
    } finally {
      dispatch(showLoader(false));
    }
  };

  // Generate payment link and message for manual sending
  const handleGenerateLink = async (member) => {
    setSelectedMember(member);
    setGeneratingLink(true);
    setLinkGeneratorOpen(true);
    setGeneratedLink(null);
    setCopiedField(null);
    
    try {
      // Generate payment link via API
      const result = await dispatch(sendRenewalTemplateReminder({
        userId: member._id,
        templateType: "auto",
        gracePeriodDays: 30,
        generateOnly: true // This flag tells backend to only generate link, not send
      }));
      
      const data = result?.payload?.data;
      const daysRemaining = member.daysRemaining;
      const expiryDate = member.policyStatus?.expireAt 
        ? dayjs(member.policyStatus.expireAt).format("DD/MM/YYYY")
        : "N/A";
      
      // Check if link generation was successful
      const linkAvailable = data?.paymentLink ? true : false;
      const linkError = data?.linkError || result?.payload?.message || null;
      
      if (!linkAvailable && linkError) {
        toast.warning(`Payment link issue: ${linkError}`);
      }
      
      // Generate bilingual message
      const gujaratiMsg = `પ્રિય ${member.fullName},

તમારું PPSK સભ્યપદ ${daysRemaining >= 0 ? `${daysRemaining} દિવસમાં` : `${Math.abs(daysRemaining)} દિવસ પહેલા`} ${daysRemaining >= 0 ? 'સમાપ્ત થશે' : 'સમાપ્ત થયું છે'}.

સભ્યપદ સંબંધિત સેવાઓ ચાલુ રાખવા માટે, કૃપા કરીને નવીકરણ કરો.

📅 સમાપ્તિ તારીખ: ${expiryDate}
💳 નવીકરણ લિંક: ${data?.paymentLink || 'Link not available'}

- PPSK Support Team`;

      const englishMsg = `Dear ${member.fullName},

Your PPSK membership ${daysRemaining >= 0 ? `will expire in ${daysRemaining} days` : `expired ${Math.abs(daysRemaining)} days ago`}.

To continue accessing membership-related services, please renew.

📅 Expiry Date: ${expiryDate}
💳 Renewal Link: ${data?.paymentLink || 'Link not available'}

- PPSK Support Team`;

      const combinedMsg = `${gujaratiMsg}

---

${englishMsg}`;

      setGeneratedLink({
        paymentLink: data?.paymentLink || null,
        paymentAmount: data?.paymentAmount || null,
        daysRemaining: daysRemaining,
        expiryDate: expiryDate,
        gujaratiMessage: gujaratiMsg,
        englishMessage: englishMsg,
        combinedMessage: combinedMsg,
        memberName: member.fullName,
        memberPhone: member.phoneNumber,
        ppskId: member.ppskId,
        linkError: linkError
      });
    } catch (error) {
      console.error("Error generating link:", error);
      toast.error("Failed to generate payment link");
    } finally {
      setGeneratingLink(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // Toggle member selection
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Select all
  const toggleSelectAll = () => {
    if (selectedMembers.length === members.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(members.map(m => m._id));
    }
  };

  // Format days remaining
  const formatDaysRemaining = (days) => {
    if (days === null || days === undefined) return "N/A";
    if (days < -30) return `${Math.abs(days)} days expired`;
    if (days < 0) return `${Math.abs(days)} days (grace)`;
    if (days === 0) return "Today";
    return `${days} days`;
  };

  // Get status label
  const getStatusLabel = (status) => {
    const labels = {
      active: "Active",
      expiring_soon: "Expiring Soon",
      grace_period: "Grace Period",
      expired: "Expired",
      never_paid: "Never Paid",
    };
    return labels[status] || status;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchMembers();
    fetchStats();
  }, [page, rowsPerPage]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const debounce = setTimeout(() => {
      setPage(1);
      fetchMembers();
    }, 500);
    return () => clearTimeout(debounce);
  }, [filters]);

  // Table columns - with Select All checkbox
  const tableHeaders = [
    <Checkbox
      key="select-all"
      checked={members.length > 0 && selectedMembers.length === members.length}
      indeterminate={selectedMembers.length > 0 && selectedMembers.length < members.length}
      onChange={toggleSelectAll}
      size="small"
      sx={{ color: "white", "&.Mui-checked": { color: "white" }, "&.MuiCheckbox-indeterminate": { color: "white" } }}
    />,
    "PPSK ID",
    "Name",
    "Phone",
    "Days Left",
    "Status",
    "Last Reminder",
    "Actions",
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight={600} color="primary">
          <NotificationsActiveIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Renewal Reminders
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchMembers(); fetchStats(); }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Last Send Status */}
      {lastSendResult && (
        <Paper 
          sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 2, 
            bgcolor: lastSendResult.success ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${lastSendResult.success ? '#86efac' : '#fca5a5'}`
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {lastSendResult.success ? (
                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 28 }} />
              ) : (
                <ErrorIcon sx={{ color: '#dc2626', fontSize: 28 }} />
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color={lastSendResult.success ? '#16a34a' : '#dc2626'}>
                  {lastSendResult.success 
                    ? (lastSendResult.isBulk 
                        ? `Bulk Send Complete: ${lastSendResult.successful}/${lastSendResult.total} sent successfully`
                        : `Message Sent to ${lastSendResult.memberName}`)
                    : (lastSendResult.isBulk ? 'Bulk Send Failed' : `Failed to send to ${lastSendResult.memberName}`)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(lastSendResult.sentAt).format('DD/MM/YYYY HH:mm:ss')}
                  {lastSendResult.templateUsed && ` | Template: ${lastSendResult.templateUsed.replace(/_/g, ' ')}`}
                  {lastSendResult.messageStatus && ` | Status: ${lastSendResult.messageStatus}`}
                  {lastSendResult.paymentLink && ` | Payment Link: Generated`}
                  {lastSendResult.error && ` | Error: ${lastSendResult.error}`}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setLastSendResult(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Reminder Activity Stats */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#f8fafc' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          <SendIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
          Reminder Activity
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h5" fontWeight={700} color="primary">
                {stats.reminders?.today?.total || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Sent Today</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h5" fontWeight={700} color="secondary">
                {stats.reminders?.thisWeek?.total || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">This Week</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {stats.reminders?.thisMonth?.total || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">This Month</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h5" fontWeight={700} color="warning.main">
                {stats.members?.total || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Total Members</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Member Status Overview
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard color="#dc2626" onClick={() => setFilters(f => ({ ...f, daysFilter: "7", statusFilter: "all" }))}>
              <Box sx={{ p: 1, borderRadius: "8px", bgcolor: "#dc262620" }}>
                <WarningAmberIcon sx={{ color: "#dc2626", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#dc2626">
                  {stats.members?.expiringIn7Days || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Expiring 7 days
                </Typography>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard color="#ea580c" onClick={() => setFilters(f => ({ ...f, daysFilter: "30", statusFilter: "all" }))}>
              <Box sx={{ p: 1, borderRadius: "8px", bgcolor: "#ea580c20" }}>
                <TimerIcon sx={{ color: "#ea580c", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#ea580c">
                  {stats.members?.expiringIn30Days || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Expiring 30 days
                </Typography>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard color="#d97706" onClick={() => setFilters(f => ({ ...f, daysFilter: "", statusFilter: "grace_period" }))}>
              <Box sx={{ p: 1, borderRadius: "8px", bgcolor: "#d9770620" }}>
                <ErrorIcon sx={{ color: "#d97706", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#d97706">
                  {stats.members?.inGracePeriod || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Grace Period
                </Typography>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard color="#dc2626" onClick={() => setFilters(f => ({ ...f, daysFilter: "", statusFilter: "expired" }))}>
              <Box sx={{ p: 1, borderRadius: "8px", bgcolor: "#dc262620" }}>
                <BlockIcon sx={{ color: "#dc2626", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#dc2626">
                  {stats.members?.expired || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Expired
                </Typography>
              </Box>
            </StatCard>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <StatCard color="#16a34a" onClick={() => setFilters(f => ({ ...f, daysFilter: "", statusFilter: "active" }))}>
              <Box sx={{ p: 1, borderRadius: "8px", bgcolor: "#16a34a20" }}>
                <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} color="#16a34a">
                  {stats.members?.active || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                  Active (30+ days)
                </Typography>
              </Box>
            </StatCard>
          </Grid>
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Name, PPSK ID, Phone..."
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Days Filter</InputLabel>
              <Select
                value={filters.daysFilter}
                label="Days Filter"
                onChange={(e) => setFilters(f => ({ ...f, daysFilter: e.target.value }))}
              >
                {daysFilterOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.statusFilter}
                label="Status"
                onChange={(e) => setFilters(f => ({ ...f, statusFilter: e.target.value }))}
              >
                {statusFilterOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={(e) => setFilters(f => ({ ...f, sortBy: e.target.value }))}
              >
                <MenuItem value="daysRemaining">Days Remaining</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="createdAt">Created Date</MenuItem>
                <MenuItem value="expireAt">Expiry Date</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectedMembers.length > 0 && (
              <Button
                variant="contained"
                color="success"
                startIcon={<WhatsAppIcon />}
                onClick={handleBulkSend}
                disabled={sendingReminder}
                fullWidth
              >
                Send to {selectedMembers.length} Members
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <CommonTable headerList={tableHeaders}>
          {members.length > 0 ? (
            members.map((member, index) => (
              <TableRow key={member._id} hover>
                <StyledTableCell>
                  <Checkbox
                    checked={selectedMembers.includes(member._id)}
                    onChange={() => toggleMemberSelection(member._id)}
                    size="small"
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {member.ppskId || "N/A"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                      {member.fullName?.charAt(0) || "?"}
                    </Avatar>
                    <Typography variant="body2">
                      {member.fullName || "N/A"}
                    </Typography>
                  </Box>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2">
                    {member.phoneNumber || "N/A"}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <DaysChip
                    days={member.daysRemaining}
                    label={formatDaysRemaining(member.daysRemaining)}
                    size="small"
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <StatusChip
                    statustype={member.statusCategory}
                    label={getStatusLabel(member.statusCategory)}
                    size="small"
                  />
                </StyledTableCell>
                <StyledTableCell>
                  {member.lastReminder ? (
                    <Tooltip title={dayjs(member.lastReminder.sentAt).format("DD/MM/YYYY HH:mm")}>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(member.lastReminder.sentAt).fromNow()}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">Never</Typography>
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => fetchMemberDetails(member._id)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate Link & Copy Message">
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleGenerateLink(member)}
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Send WhatsApp Reminder">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleSendReminder(member)}
                        disabled={!member.phoneNumber}
                      >
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </StyledTableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <StyledTableCell colSpan={8}>
                <DataNotFound />
              </StyledTableCell>
            </TableRow>
          )}
        </CommonTable>

        {/* Pagination */}
        {totalCount > 0 && (
          <Box sx={{ p: 2 }}>
            <CustomPagination
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Box>
        )}
      </Paper>

      {/* Send Reminder Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">
            <WhatsAppIcon sx={{ mr: 1, verticalAlign: "middle", color: "#25D366" }} />
            Send WhatsApp Reminder
          </Typography>
          <IconButton onClick={() => setSendDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedMember && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 48, height: 48 }}>
                  {selectedMember.fullName?.charAt(0) || "?"}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {selectedMember.fullName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedMember.phoneNumber} | {selectedMember.ppskId}
                  </Typography>
                  <Chip 
                    size="small" 
                    label={`${selectedMember.daysRemaining ?? 'N/A'} days ${selectedMember.daysRemaining >= 0 ? 'remaining' : 'expired'}`}
                    color={selectedMember.daysRemaining > 7 ? "success" : selectedMember.daysRemaining > 0 ? "warning" : "error"}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />

              {/* Template Selection */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Select Template</InputLabel>
                <Select
                  value={selectedTemplateType}
                  label="Select Template"
                  onChange={(e) => setSelectedTemplateType(e.target.value)}
                >
                  <MenuItem value="auto">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Auto Select (Recommended)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Automatically selects template based on days remaining
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />
                  <MenuItem value="renewal_30_days">30 Days Reminder</MenuItem>
                  <MenuItem value="renewal_7_days">7 Days Reminder</MenuItem>
                  <MenuItem value="renewal_1_day">1 Day Final Notice</MenuItem>
                  <MenuItem value="policy_expired">Policy Expired</MenuItem>
                  <MenuItem value="grace_period_warning">Grace Period Warning</MenuItem>
                  <MenuItem value="membership_lapsed">Membership Lapsed</MenuItem>
                </Select>
              </FormControl>

              {/* Template Info */}
              <Paper sx={{ p: 2, bgcolor: "#f0fdf4", borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Template Info (Meta Approved - Gujarati)
                </Typography>
                {selectedTemplateType === "auto" ? (
                  <Box>
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      Auto-Selection Logic:
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                      • 8-30 days remaining → 30 Days Reminder<br/>
                      • 2-7 days remaining → 7 Days Reminder<br/>
                      • 1 day remaining → 1 Day Final Notice<br/>
                      • 0 days (today) → Policy Expired<br/>
                      • 1-30 days expired → Grace Period Warning<br/>
                      • 30+ days expired → Membership Lapsed
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2">
                    Selected: <strong>{selectedTemplateType.replace(/_/g, ' ').toUpperCase()}</strong>
                  </Typography>
                )}
              </Paper>

              <Paper sx={{ p: 2, bgcolor: "#fff7ed", borderRadius: 2 }}>
                <Typography variant="caption" color="warning.dark">
                  ⚠️ This will send a WhatsApp message using Meta-approved template in Gujarati language.
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSendDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={confirmSendReminder}
            variant="contained"
            color="success"
            startIcon={sendingReminder ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={sendingReminder}
          >
            {sendingReminder ? "Sending..." : "Send WhatsApp"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Member Details Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle component="div" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Member Renewal Details</Typography>
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {memberDetails && (
            <Box>
              {/* Member Info */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: "#f8fafc" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar sx={{ width: 64, height: 64, fontSize: 24 }}>
                        {memberDetails.member?.fullName?.charAt(0) || "?"}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {memberDetails.member?.fullName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {memberDetails.member?.ppskId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {memberDetails.member?.phoneNumber} | {memberDetails.member?.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Paper sx={{ p: 1.5, minWidth: 100, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">Days Left</Typography>
                        <Typography variant="h6" fontWeight={600} color={memberDetails.member?.daysRemaining > 0 ? "success.main" : "error.main"}>
                          {memberDetails.member?.daysRemaining ?? "N/A"}
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 1.5, minWidth: 100, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <StatusChip
                          statustype={memberDetails.member?.statusCategory}
                          label={getStatusLabel(memberDetails.member?.statusCategory)}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Paper>
                      <Paper sx={{ p: 1.5, minWidth: 100, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">Expires On</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {memberDetails.member?.policyStatus?.expireAt 
                            ? dayjs(memberDetails.member.policyStatus.expireAt).format("DD/MM/YYYY")
                            : "N/A"}
                        </Typography>
                      </Paper>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Tabs for History */}
              <Tabs value={detailTabValue} onChange={(e, newValue) => setDetailTabValue(newValue)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tab icon={<PaymentIcon />} label="Payment History" iconPosition="start" />
                <Tab icon={<HistoryIcon />} label="Reminder History" iconPosition="start" />
              </Tabs>

              {/* Payment History Tab */}
              {detailTabValue === 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Payment History
                  </Typography>
                  {memberDetails.transactions?.length > 0 ? (
                    memberDetails.transactions.map((txn, idx) => (
                      <Paper key={idx} sx={{ p: 2, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            ₹{txn.amount} - {txn.status?.toLowerCase() === "success" ? "✅ Success" : txn.status}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(txn.transactionDate || txn.createdAt).format("DD/MM/YYYY HH:mm")} | Receipt: {txn.receiptNo || "N/A"}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Gateway: {txn.transactionGateway || "N/A"} | TxnID: {txn.transactionId || "N/A"}
                          </Typography>
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No payment history</Typography>
                  )}
                </Box>
              )}

              {/* Reminder History Tab */}
              {detailTabValue === 1 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Reminder History
                  </Typography>
                  {memberDetails.reminders?.length > 0 ? (
                    memberDetails.reminders.map((rem, idx) => (
                      <Paper key={idx} sx={{ p: 2, mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {rem.reminderType?.toUpperCase()} - {rem.status === "sent" ? "✅ Sent" : rem.status === "failed" ? "❌ Failed" : rem.status}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(rem.sentAt).format("DD/MM/YYYY HH:mm")} | By: {rem.sentBy?.fullName || "System"}
                          </Typography>
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No reminders sent</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailOpen(false)} variant="outlined">
            Close
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            onClick={() => {
              setDetailOpen(false);
              handleSendReminder(memberDetails?.member);
            }}
            disabled={!memberDetails?.member?.phoneNumber}
          >
            Send WhatsApp Reminder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Generator Dialog */}
      <Dialog open={linkGeneratorOpen} onClose={() => setLinkGeneratorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">
            <LinkIcon sx={{ mr: 1, verticalAlign: "middle", color: "#ed6c02" }} />
            Generate Payment Link & Message
          </Typography>
          <IconButton onClick={() => setLinkGeneratorOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {generatingLink ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Generating payment link...</Typography>
            </Box>
          ) : generatedLink ? (
            <Box>
              {/* Member Info */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: "#f8fafc" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {generatedLink.memberName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {generatedLink.memberPhone} | {generatedLink.ppskId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Chip 
                        label={`${generatedLink.daysRemaining >= 0 ? generatedLink.daysRemaining + ' days left' : Math.abs(generatedLink.daysRemaining) + ' days expired'}`}
                        color={generatedLink.daysRemaining > 7 ? "success" : generatedLink.daysRemaining > 0 ? "warning" : "error"}
                        size="small"
                      />
                      <Chip 
                        label={`Expires: ${generatedLink.expiryDate}`}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Payment Link */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: generatedLink.paymentLink ? "#ecfdf5" : "#fef2f2", border: `1px solid ${generatedLink.paymentLink ? "#10b981" : "#ef4444"}` }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} color={generatedLink.paymentLink ? "success.main" : "error.main"}>
                    💳 Payment Link {!generatedLink.paymentLink && "⚠️"}
                  </Typography>
                  <Button
                    size="small"
                    variant={copiedField === 'link' ? "contained" : "outlined"}
                    color="success"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(generatedLink.paymentLink, 'link')}
                    disabled={!generatedLink.paymentLink}
                  >
                    {copiedField === 'link' ? 'Copied!' : 'Copy Link'}
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  value={generatedLink.paymentLink || 'Link not available'}
                  InputProps={{ readOnly: true }}
                  sx={{ bgcolor: "white" }}
                  error={!generatedLink.paymentLink}
                />
                {generatedLink.paymentAmount && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Amount: ₹{generatedLink.paymentAmount}
                  </Typography>
                )}
                {generatedLink.linkError && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                    ⚠️ Error: {generatedLink.linkError}
                  </Typography>
                )}
              </Paper>

              {/* Combined Message */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: "#fffbeb", border: "1px solid #f59e0b" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="warning.dark">
                    📱 Complete Message (Gujarati + English)
                  </Typography>
                  <Button
                    size="small"
                    variant={copiedField === 'combined' ? "contained" : "outlined"}
                    color="warning"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(generatedLink.combinedMessage, 'combined')}
                  >
                    {copiedField === 'combined' ? 'Copied!' : 'Copy Message'}
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={generatedLink.combinedMessage}
                  InputProps={{ readOnly: true }}
                  sx={{ bgcolor: "white", "& .MuiInputBase-input": { fontSize: "13px" } }}
                />
              </Paper>

              {/* Gujarati Only */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    🇮🇳 Gujarati Message Only
                  </Typography>
                  <Button
                    size="small"
                    variant={copiedField === 'gujarati' ? "contained" : "outlined"}
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(generatedLink.gujaratiMessage, 'gujarati')}
                  >
                    {copiedField === 'gujarati' ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={generatedLink.gujaratiMessage}
                  InputProps={{ readOnly: true }}
                  sx={{ bgcolor: "#f9fafb", "& .MuiInputBase-input": { fontSize: "13px" } }}
                />
              </Paper>

              {/* English Only */}
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    🇬🇧 English Message Only
                  </Typography>
                  <Button
                    size="small"
                    variant={copiedField === 'english' ? "contained" : "outlined"}
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToClipboard(generatedLink.englishMessage, 'english')}
                  >
                    {copiedField === 'english' ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={generatedLink.englishMessage}
                  InputProps={{ readOnly: true }}
                  sx={{ bgcolor: "#f9fafb", "& .MuiInputBase-input": { fontSize: "13px" } }}
                />
              </Paper>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">Failed to generate link. Please try again.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">
            Copy the message and send manually via WhatsApp
          </Typography>
          <Button onClick={() => setLinkGeneratorOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reminders;
